import { AfterViewInit, Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { Observable, catchError, forkJoin, map } from 'rxjs';
import { toCamelCaseString } from 'src/app/shared/helpers/stringToCamel.helper';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { PreInstallLoad } from '../../models/load-case.model';
import { LoadCaseService } from '../../services/load-case.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { WellConfigService } from 'src/app/shared/services/well-config.service';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { FluidsService } from 'src/app/perical/services/fluids.service';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';

@Component({
    selector: 'app-pre-install-loads',
    templateUrl: './pre-install-loads.component.html',
    providers: [ConfirmationService],
    standalone: false
})
export class PreInstallLoadsComponent implements OnInit, AfterViewInit, OnDestroy {
  public loadCases: Partial<PreInstallLoad>[];
  public selectedLoad: Partial<PreInstallLoad>;
  public selectedLoadIndex: number;
  public selectedLoadDetails = signal<PreInstallLoad | null>(null);
  public emitInitialValueOnInit = false;
  public userUnits: UserUnitsModel;
  public isCemented: boolean;
  public hangerMd: number;
  public shoeDepth: number;
  public annularFLuidDensity: number;
  isLoading = signal<boolean>(false);

  private isNewLoad = false;
  private isUpdate = false;
  private skipSave = false;

  @Input()
  public componentId: string;

 async ngOnInit(): Promise<void> {
    this.userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
    this.getLoadCases();
  }

  constructor(
    private _loadCaseService: LoadCaseService,
    private _confirmationService: ConfirmationService,
    private _signalRService: SignalRService,
    private _storeService: StoreService,
    private _wellConfigService: WellConfigService,
    private _messenger: MediatorService,
    private _fluidsService: FluidsService
  ) { }

  ngAfterViewInit(): void {
    // Ensure details are loaded once the view is ready, independent of resize events.
    if (this.selectedLoad && !this.selectedLoadDetails()) {
      this.onLoadSelect({ value: this.selectedLoad });
    }
  }

  signalRfunc (data: any)  {
    if (data.action === PeriforOnChangeMessages.REFRESH_TUBULARS_LIST || data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.getLoadCases();
    }
  }

  private getLoadCases() {
    this.isLoading.set(true);
    const sources = [
      this._wellConfigService.getTubular() as Observable<any>,
      this._loadCaseService.getLoadCasesSummary(false) as Observable<any>
    ];

    forkJoin(sources).pipe(
      map(([currentString, loads]) => {
        this.loadCases = loads;
        this.selectedLoad = this.loadCases ? this.isNewLoad ? this.loadCases[this.loadCases.length - 1] :
        this.isUpdate ? this.loadCases.find(x => x['index'] === this.selectedLoad['index']) : this.loadCases[0] : null;
        this.skipSave = false;
        this.isNewLoad = false;

        this.hangerMd = currentString.hangerMd;
        this.shoeDepth = currentString.shoeMd - 0.1;
        const annularFluidId = currentString.annularFluidId;

        if (annularFluidId) {
        this._fluidsService.getFluidById(annularFluidId).subscribe(fluid => {
          this.annularFLuidDensity = fluid.state['nominalDensity'];
        });
      }

        this.isCemented = currentString.hasCement;
      if (this.selectedLoad) {
        this.onLoadSelect({ value: this.selectedLoad });
      } else {
        this.clearLoadDetails();
      }
      this.isLoading.set(false);
    }),
    catchError(err => {
      this.isLoading.set(false);
      return err;
    })).subscribe();
  }

  private remapLoad(load: any, isUpdate: boolean, index?: number): PreInstallLoad {
    const type = isUpdate ? load.data.type : load['type'];
    let loadType;
    switch (type) {
      case 'Overpull':
        loadType = 'OverpullForce';
        break;
      case 'RunInHole':
        loadType = 'RunInHoleSpeed';
        break;
      case 'GreenCementTest':
        loadType = 'PumpPressure';
        break;
    }

    const loadCase: any = {
      name: load.name,
      index: index,
      data: {
        [loadType]: isUpdate ? load.data[loadType] : load[toCamelCaseString(loadType)],
        type: isUpdate ? load.data.type : load['type'],
        IsFlotation: isUpdate ? load.data.IsFlotation : load['isFlotation'],
        InternalFluidDensity: isUpdate ? load.data.InternalFluidDensity : load['internalFluidDensity'],
        HasBottomCap: isUpdate ? load.data.HasBottomCap : load['hasBottomCap'],
        HasFlotationPlug: isUpdate ? load.data.HasFlotationPlug : load['hasFlotationPlug'],
        PlugDepth: isUpdate ? load.data.PlugDepth : load['plugDepth'],
        FluidDensity: isUpdate ? load.data.FluidDensity : load['fluidDensity'],
        HasPlug: isUpdate? load.data.HasPlug : load['hasPlug']
      }
    }
    return loadCase;
  }

  public updateOrCreateLoad(load: PreInstallLoad): void {
    if (this.skipSave) {
      this.skipSave = false;
      return;
    }

    const loadIndex = this.loadCases.findIndex(x => x['index'] === this.selectedLoad['index'] && (this.selectedLoad.data || this.selectedLoad['type']));
    if (loadIndex < 0) {
      this.isLoading.set(true);
      this._loadCaseService.addLoadCaseDefinition(load).subscribe(() => {
        this.isNewLoad = true;
        this.getLoadCases();
        this.isLoading.set(false);
      });
    }  else {
      this._loadCaseService.updatePreinstallLoadCase(this.remapLoad(load, true, this.selectedLoad['index'])).subscribe(() => {
        this.isUpdate = true;
        this.skipSave = true;
        this.getLoadCases();
      });
    }
        this._messenger.publish(new DataSavedMessage('Pre-Install Load Cases'));
  }

  public onLoadSelect(e) {
    if (e.value == null) {
      return;
    }
    this.selectedLoadDetails.set(null);
    // this.skipSave = true;
    this._loadCaseService.getPreInstallLoadCase(e.value.index).subscribe(load => {
      this.selectedLoad = this.loadCases.find(x => x.name === load.name);
      this.selectedLoadDetails.set(this.remapLoad(load, false));
    });
  }

  public addLoad() {
    if (this.loadCases.length > 0 && !this.loadCases[this.loadCases.length - 1]['type']) {
      return;
    }
    this.skipSave = false;
    const newLoad: PreInstallLoad = {
      name: 'New Load',
      data: null
    };
    this.loadCases.push(newLoad);
    this.selectedLoad = this.loadCases[this.loadCases.length - 1];
    this.selectedLoadDetails.set(newLoad);
  }

  public deleteLoad(load: PreInstallLoad): void {
    const loadIndex = this.loadCases.find(x => x.name === load.name)['index'];
    if (typeof loadIndex !== 'number' || loadIndex < 0) {
      this.selectedLoadAfterDelete(loadIndex);
      return;
    } else {
      this._confirmationService.confirm({
        message: 'Are you sure that you want to delete load case "' + load.name.bold() + '"?',
        accept: () => {
          this._loadCaseService.deleteLoadCase(loadIndex).subscribe(() => {
            this.selectedLoadAfterDelete(loadIndex);
          });
        }
      });
    }
  }

  private selectedLoadAfterDelete(loadIndex: number) {
    const index = this.loadCases.indexOf(this.loadCases.find(l => l['index'] === loadIndex));
    this.loadCases.splice(index, 1);
    this.loadCases = [...this.loadCases];
    this.selectedLoad = this.loadCases.length !== 0 ? this.loadCases[0] : null;
    if (this.selectedLoad !== null) {
      this._loadCaseService.getPreInstallLoadCase(this.selectedLoad['index']).subscribe(load => {
        this.selectedLoadDetails.set(this.remapLoad(load, false));
      });
    } else {
      this.clearLoadDetails();
    }
  }

  public cloneLoad(load: PreInstallLoad): void {
    const index = load['index'];
    const loadCase = { name: load.name + ' - Copy', index: index }
    this._loadCaseService.cloneLoadCaseDefinition(loadCase).subscribe(res => {
      this._messenger.publish(new DataSavedMessage('Pre-Install Load Cases'));
      this._loadCaseService.getPreInstallLoadCase(res['index']).subscribe(() => {
        this.getLoadCases();
      });
    });
  }

  clearLoadDetails(): void {
    this.selectedLoadDetails.set(null);
  }

  ngOnDestroy(): void {
    this.signalRfunc = null;
  }
}
